import React, { useCallback, useEffect, useState } from 'react';
import {
  PlusIcon, LinkIcon, ClipboardDocumentIcon, CheckIcon, TrashIcon, PencilSquareIcon,
  ArrowTopRightOnSquareIcon, QrCodeIcon, ClockIcon, VideoCameraIcon, MapPinIcon, CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { BookingSettings, BookingLink } from '../../types/appointments';
import { appointmentService } from '../../services/appointmentService';
import Modal from '../common/Modal';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';
import { Switch } from '../common/Switch';

interface BookingLinkGeneratorProps {
  settings: BookingSettings[];
  onSettingsChange: () => void;
  onCreateSettings?: () => void;
  onEditSettings?: (setting: BookingSettings) => void;
  appointmentCounts?: Record<string, number>;
}

/**
 * Foglalási beállítások és linkjeik – egy helyen kezelve:
 * új link, másolás, QR, megnyitás, link törlése; beállítás szerkesztése, aktiválás, törlése.
 */
const BookingLinkGenerator: React.FC<BookingLinkGeneratorProps> = ({
  settings, onSettingsChange, onCreateSettings, onEditSettings, appointmentCounts = {},
}) => {
  const [links, setLinks] = useState<Record<string, BookingLink[]>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [qrLink, setQrLink] = useState<BookingLink | null>(null);
  const [linkToDelete, setLinkToDelete] = useState<BookingLink | null>(null);
  const [settingToDelete, setSettingToDelete] = useState<BookingSettings | null>(null);

  const url = (l: BookingLink) => appointmentService.generateBookingUrl(l.publicId);

  const loadLinks = useCallback(async () => {
    const entries = await Promise.all(settings.map(async s => [s.id!, await appointmentService.getBookingLinks(s.id!)] as const));
    setLinks(Object.fromEntries(entries));
  }, [settings]);

  useEffect(() => { loadLinks(); }, [loadLinks]);

  const copy = async (l: BookingLink) => {
    try {
      await navigator.clipboard.writeText(url(l));
      setCopied(l.id);
      setTimeout(() => setCopied(null), 1500);
    } catch { toast.error('Nem sikerült a vágólapra másolni'); }
  };

  const createLink = async (settingsId: string) => {
    setBusy(settingsId);
    try {
      const l = await appointmentService.createBookingLink(settingsId);
      await loadLinks();
      await copy(l);
      toast.success('Új link létrehozva és a vágólapra másolva');
    } catch { toast.error('Nem sikerült létrehozni a linket'); }
    finally { setBusy(null); }
  };

  const toggleLink = async (l: BookingLink) => {
    try {
      await appointmentService.setBookingLinkActive(l.id, !l.isActive);
      await loadLinks();
    } catch { toast.error('Nem sikerült módosítani'); }
  };

  const confirmDeleteLink = async () => {
    if (!linkToDelete) return;
    try {
      await appointmentService.deleteBookingLink(linkToDelete.id);
      toast.success('Link törölve – a régi URL már nem nyitható meg');
      setLinkToDelete(null);
      await loadLinks();
    } catch { toast.error('Nem sikerült törölni'); }
  };

  const toggleSetting = async (s: BookingSettings) => {
    try {
      await appointmentService.updateBookingSettings(s.id!, { isActive: !s.isActive });
      onSettingsChange();
    } catch { toast.error('Nem sikerült módosítani'); }
  };

  const confirmDeleteSetting = async () => {
    if (!settingToDelete) return;
    try {
      const ls = links[settingToDelete.id!] || [];
      await Promise.all(ls.map(l => appointmentService.deleteBookingLink(l.id)));
      await appointmentService.deleteBookingSettings(settingToDelete.id!);
      toast.success('Beállítás és a linkjei törölve');
      setSettingToDelete(null);
      onSettingsChange();
    } catch { toast.error('Nem sikerült törölni'); }
  };

  const meetingLabel = (s: BookingSettings) => {
    const t = s.meetingOptions?.defaultMeetingType;
    if (!s.meetingOptions?.enableOnlineMeeting || t === 'in_person') return { icon: MapPinIcon, text: 'Személyes' };
    if (t === 'hybrid') return { icon: VideoCameraIcon, text: 'Személyes vagy online' };
    return { icon: VideoCameraIcon, text: 'Online' };
  };

  return (
    <div className="space-y-4">
      {settings.length === 0 && (
        <div className="crm-card p-10 text-center">
          <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-400 flex items-center justify-center mx-auto mb-3">
            <CalendarDaysIcon className="w-5 h-5" />
          </div>
          <h3 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">Még nincs foglalási beállítás</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-sm mx-auto">
            Egy beállítás = egy szolgáltatás (pl. „Konzultáció, 60 perc”). Hozz létre egyet, és kapsz hozzá megosztható linket.
          </p>
          {onCreateSettings && (
            <button onClick={onCreateSettings} className="crm-btn crm-btn-primary mt-4">
              <PlusIcon className="w-4 h-4" />Első beállítás
            </button>
          )}
        </div>
      )}

      {settings.map(s => {
        const ls = links[s.id!] || [];
        const ml = meetingLabel(s);
        return (
          <section key={s.id} className={`crm-card overflow-hidden ${!s.isActive ? 'opacity-70' : ''}`}>
            {/* Fejléc */}
            <header className="flex flex-wrap items-start gap-3 px-4 py-3 border-b border-[var(--crm-border)]">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 min-w-0">
                  <h3 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50 truncate">{s.title}</h3>
                  <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${s.isActive ? 'bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'}`}>
                    {s.isActive ? 'Aktív' : 'Inaktív'}
                  </span>
                </div>
                {s.description && <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{s.description}</p>}
                <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-400">
                  <span className="inline-flex items-center gap-1"><ClockIcon className="w-3.5 h-3.5" />{s.duration} perc</span>
                  <span className="inline-flex items-center gap-1"><CalendarDaysIcon className="w-3.5 h-3.5" />{s.availableHours?.start}–{s.availableHours?.end}</span>
                  <span className="inline-flex items-center gap-1"><ml.icon className="w-3.5 h-3.5" />{ml.text}</span>
                  {s.requireConfirmation && <span>jóváhagyással</span>}
                  {(appointmentCounts[s.id!] || 0) > 0 && <span className="tabular-nums">{appointmentCounts[s.id!]} foglalás</span>}
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <label className="flex items-center gap-1.5 mr-1 cursor-pointer" title={s.isActive ? 'Deaktiválás' : 'Aktiválás'}>
                  <Switch checked={!!s.isActive} onChange={() => toggleSetting(s)} size="sm" />
                </label>
                {onEditSettings && (
                  <button onClick={() => onEditSettings(s)} className="crm-btn crm-btn-secondary h-8 px-2.5 text-xs" title="Szerkesztés">
                    <PencilSquareIcon className="w-3.5 h-3.5" /><span className="hidden sm:inline">Szerkesztés</span>
                  </button>
                )}
                <button onClick={() => setSettingToDelete(s)} className="crm-btn crm-btn-secondary h-8 px-2.5 text-xs text-red-700 dark:text-red-400" title="Beállítás törlése">
                  <TrashIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            </header>

            {/* Linkek */}
            <div className="px-4 py-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold tracking-[0.05em] uppercase text-gray-500 dark:text-gray-400">Foglalási linkek</span>
                <button onClick={() => createLink(s.id!)} disabled={busy === s.id || !s.isActive} className="crm-btn crm-btn-primary h-8 px-2.5 text-xs">
                  <PlusIcon className="w-3.5 h-3.5" />Új link
                </button>
              </div>
              {ls.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-2">Még nincs link. Az „Új link” egy megosztható URL-t készít (és a vágólapra másolja).</p>
              ) : (
                <ul className="divide-y divide-[var(--crm-border)] -mx-4">
                  {ls.map(l => (
                    <li key={l.id} className={`flex flex-wrap sm:flex-nowrap items-center gap-2 px-4 py-2 ${!l.isActive ? 'opacity-60' : ''}`}>
                      <LinkIcon className="w-4 h-4 text-gray-400 flex-shrink-0 hidden sm:block" />
                      <button onClick={() => copy(l)} className="min-w-0 flex-1 text-left font-mono text-[13px] text-gray-800 dark:text-gray-200 truncate hover:text-primary-700 dark:hover:text-primary-300" title="Kattints a másoláshoz">
                        {url(l).replace(/^https?:\/\//, '')}
                      </button>
                      <span className="text-xs text-gray-400 tabular-nums hidden md:inline">{l.createdAt.toLocaleDateString('hu-HU')}</span>
                      <div className="flex items-center gap-1 flex-shrink-0 w-full sm:w-auto">
                        <button onClick={() => copy(l)} className="crm-btn crm-btn-secondary h-8 px-2 text-xs" title="Másolás">
                          {copied === l.id ? <CheckIcon className="w-3.5 h-3.5 text-green-600" /> : <ClipboardDocumentIcon className="w-3.5 h-3.5" />}
                          <span className="sm:hidden">{copied === l.id ? 'Másolva' : 'Másolás'}</span>
                        </button>
                        <button onClick={() => setQrLink(l)} className="crm-btn crm-btn-secondary h-8 px-2 text-xs" title="QR-kód">
                          <QrCodeIcon className="w-3.5 h-3.5" /><span className="sm:hidden">QR</span>
                        </button>
                        <a href={url(l)} target="_blank" rel="noopener noreferrer" className="crm-btn crm-btn-secondary h-8 px-2 text-xs" title="Megnyitás új lapon">
                          <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" /><span className="sm:hidden">Megnyit</span>
                        </a>
                        <button onClick={() => toggleLink(l)} className="crm-btn crm-btn-secondary h-8 px-2 text-xs" title={l.isActive ? 'Link felfüggesztése' : 'Link aktiválása'}>
                          {l.isActive ? 'Felfüggeszt' : 'Aktivál'}
                        </button>
                        <button onClick={() => setLinkToDelete(l)} className="crm-btn crm-btn-secondary h-8 px-2 text-xs text-red-700 dark:text-red-400" title="Link törlése">
                          <TrashIcon className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        );
      })}

      {/* QR modal */}
      <Modal isOpen={!!qrLink} onClose={() => setQrLink(null)} title="QR-kód a foglalási linkhez" size="sm">
        {qrLink && (
          <div className="text-center space-y-3">
            <div className="inline-block p-3 bg-white rounded-lg border border-[var(--crm-border)]">
              <img src={appointmentService.generateQRCode(url(qrLink))} alt="QR" className="w-48 h-48" />
            </div>
            <p className="font-mono text-xs text-gray-600 dark:text-gray-400 break-all">{url(qrLink)}</p>
            <div className="flex gap-2 justify-center">
              <a href={appointmentService.generateQRCode(url(qrLink))} download={`foglalas-${qrLink.publicId}.png`} className="crm-btn crm-btn-secondary">Letöltés</a>
              <button onClick={() => copy(qrLink)} className="crm-btn crm-btn-primary">Link másolása</button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDeleteModal
        isOpen={!!linkToDelete}
        onClose={() => setLinkToDelete(null)}
        onConfirm={confirmDeleteLink}
        title="Link törlése"
        deleteMessage="Biztosan törlöd ezt a foglalási linket?"
        deleteWarning="Aki megkapta, annak a link ezután nem nyílik meg. A meglévő foglalások megmaradnak."
      />
      <ConfirmDeleteModal
        isOpen={!!settingToDelete}
        onClose={() => setSettingToDelete(null)}
        onConfirm={confirmDeleteSetting}
        title="Beállítás törlése"
        deleteMessage={`Biztosan törlöd a(z) „${settingToDelete?.title}” beállítást?`}
        deleteWarning="Az összes hozzá tartozó link is törlődik. A már leadott foglalások megmaradnak."
        danger
      />
    </div>
  );
};

export default BookingLinkGenerator;
